import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Button, Card, CardContent, Stack, Chip, Grid, Alert,
  Link, Divider, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Fab, Zoom, useScrollTrigger } from '@mui/material';
import AnimacionPOSCompacto from '../components/pos/AnimacionPOSCompacto';
import DialogActivarPOS, { URL_HOLAVECI_LOCAL } from '../components/pos/DialogActivarPOS';
import DialogDescargarApp from '../components/DialogDescargarApp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MailOutlineIcon from '@mui/icons-material/MailOutline';

import { useApp } from '../context/AppContext';
import Logo from '../components/Logo';
import DialogoRegistroVeci from '../components/DialogoRegistroVeci';
import { legal } from '../api/registroApi';

export default function Landing() {
  const { entrarComoNegocio, entrarComoVeci, negocios, vecinos } = useApp();
  const navigate = useNavigate();

  const [dialogVeci, setDialogVeci] = useState(false);
  const [dialogPOS, setDialogPOS] = useState(null); // 'local' | '360' | null
  const [dialogDescarga, setDialogDescarga] = useState(null); // 'negocio' | 'veci' | null
  const [legalAbierto, setLegalAbierto] = useState(null);

  // D-23: el registro de negocio ya no se hace en esta web (HolaVeci Negocio
  // se registra en la app y solo Local en local.holaveci.co).
  const abrirRegistroPOS = (enfoque = 'local') => setDialogPOS(enfoque);

  const negocioDemo = negocios[0]; // Fruver La Esquina
  const veciDemo = vecinos[0];     // Doña Rosa

  const entrarNegocioDemo = () => {
    entrarComoNegocio(negocioDemo.id);
    navigate('/negocio');
  };
  const entrarVeciDemo = () => {
    entrarComoVeci(veciDemo.id);
    navigate('/veci');
  };

  const scrollA = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ---------- HERO con imagen fondo full ---------- */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 'auto', md: '100vh' },
          overflow: 'hidden',
        }}
      >
        {/* Imagen de fondo */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url(/images/hero_landing.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 0,
          }}
        />
        {/* Overlay para legibilidad del texto */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(15,42,34,0.45) 0%, rgba(15,42,34,0.55) 55%, rgba(15,42,34,0.75) 100%)',
            zIndex: 0,
          }}
        />

        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 1,
            py: { xs: 3, md: 5 },
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: 'auto', md: '100vh' },
          }}
        >
          {/* Navbar transparente */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: { xs: 4, md: 6 },
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Logo light />
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                onClick={() => scrollA('pilares')}
                sx={{ color: '#FBF7F0', display: { xs: 'none', md: 'inline-flex' } }}
              >
                Cómo pensamos
              </Button>
              <Button
                onClick={() => scrollA('registro')}
                sx={{ color: '#FBF7F0', display: { xs: 'none', md: 'inline-flex' } }}
              >
                Cómo entrar
              </Button>
              <Button
                onClick={() => scrollA('nominacion')}
                sx={{ color: '#FBF7F0', display: { xs: 'none', md: 'inline-flex' } }}
              >
                Recomienda tu tienda
              </Button>
              <Button
                onClick={() => scrollA('local')}
                sx={{ color: '#FBF7F0', display: { xs: 'none', md: 'inline-flex' } }}
              >
                Local
              </Button>
              <Button onClick={() => navigate('/login')} sx={{ color: '#FBF7F0' }}>
                Iniciar sesión
              </Button>
              <Button
                variant="outlined"
                onClick={() => setDialogDescarga('veci')}
                sx={{
                  color: '#FBF7F0',
                  borderColor: 'rgba(251, 247, 240, 0.6)',
                  '&:hover': { borderColor: '#FBF7F0', bgcolor: 'rgba(251, 247, 240, 0.08)' },
                }}
              >
                Soy veci
              </Button>
              <Button
                variant="contained"
                onClick={() => setDialogDescarga('negocio')}
                sx={{ bgcolor: 'secondary.main', color: 'primary.dark',
                      '&:hover': { bgcolor: 'secondary.light' } }}
              >
                Registrar negocio
              </Button>
            </Stack>
          </Box>

          <Grid container spacing={4} alignItems="center" sx={{ flexGrow: 1, pb: { xs: 3, md: 6 } }}>
            <Grid item xs={12} md={7}>
              <Chip
                label="Piloto Bucaramanga · Julio 2026"
                size="small"
                sx={{ bgcolor: 'secondary.main', color: 'primary.dark', mb: 2, fontWeight: 600 }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: 44, md: 76 },
                  lineHeight: 1.02,
                  fontFamily: `'Carter One', 'Fraunces', Georgia, serif`,
                  fontWeight: 400,
                  color: '#FBF7F0',
                  textShadow: '0 2px 24px rgba(0,0,0,0.35)',
                }}
              >
                ¡Hola veci!<br />
                <Box component="span" sx={{ color: 'secondary.main' }}>¿qué se te ofrece</Box>{' '}
                hoy?
              </Typography>
              <Typography sx={{
                mt: 3, fontSize: { xs: 16, md: 18 },
                color: 'rgba(251, 247, 240, 0.92)',
                maxWidth: 620,
                textShadow: '0 1px 12px rgba(0,0,0,0.4)',
              }}>
                Pedidos organizados y control de fiado para el tendero, el fruver, la droguería
                o la carreta de aguacates de la esquina. <b style={{ color: '#FDD835' }}>Bájala gratis</b>{' '}
                si eres vecino, pide a las tiendas de tu barrio y recomienda las que quieres ver
                en HolaVeci.
              </Typography>

              <Stack direction="row" spacing={2} sx={{ mt: 4, flexWrap: 'wrap', gap: 1.5 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => setDialogDescarga('negocio')}
                  sx={{ bgcolor: 'secondary.main', color: 'primary.dark',
                        '&:hover': { bgcolor: 'secondary.light' } }}
                >
                  Registrar mi negocio
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => setDialogDescarga('veci')}
                  sx={{
                    borderWidth: 2,
                    color: '#FBF7F0',
                    borderColor: 'rgba(251, 247, 240, 0.7)',
                    '&:hover': { borderWidth: 2, borderColor: '#FBF7F0', bgcolor: 'rgba(251, 247, 240, 0.08)' },
                  }}
                >
                  Registrarme como veci · Gratis
                </Button>
              </Stack>

              <Stack direction="row" spacing={1.5} sx={{ mt: 3, flexWrap: 'wrap', gap: 1 }}>
                <Chip label="Sin logística centralizada" variant="outlined" size="small"
                      sx={{ color: '#FBF7F0', borderColor: 'rgba(251, 247, 240, 0.5)' }} />
                <Chip label="Sin WhatsApp desorganizado" variant="outlined" size="small"
                      sx={{ color: '#FBF7F0', borderColor: 'rgba(251, 247, 240, 0.5)' }} />
                <Chip label="Fiado con historial claro" variant="outlined" size="small"
                      sx={{ color: '#FBF7F0', borderColor: 'rgba(251, 247, 240, 0.5)' }} />
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  border: '1px solid', borderColor: 'rgba(251, 247, 240, 0.25)',
                  borderRadius: 3,
                  p: 3,
                  bgcolor: 'rgba(255, 252, 243, 0.95)',
                  backdropFilter: 'blur(6px)',
                }}
              >
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                  Del barrio, para el barrio
                </Typography>
                <Typography sx={{ mt: 1, fontFamily: 'Fraunces', fontSize: 22, lineHeight: 1.3, fontWeight: 600, color: 'primary.dark' }}>
                  Tu barrio en la palma de tu mano, hecho por quienes lo habitan.
                </Typography>
                <Typography sx={{ mt: 1.5, fontSize: 15, lineHeight: 1.5, color: 'text.secondary' }}>
                  Una red barrial que crece con la confianza de siempre, ahora en el celular.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ---------- NOMINACIÓN COMUNITARIA (narrativa) ---------- */}
      <Box sx={{
        py: { xs: 6, md: 10 },
        bgcolor: '#FBF7F0',
        borderTop: '1px solid', borderColor: 'divider',
      }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="overline" color="warning.main" fontWeight={700}>
              Cómo se puebla HolaVeci
            </Typography>
            <Typography variant="h3" sx={{ fontFamily: 'Fraunces', mt: 1, lineHeight: 1.15 }}>
              Ya no más volantes ni tarjeticas en la nevera.
            </Typography>
          </Box>

          <Typography sx={{
            fontFamily: 'Fraunces', fontSize: { xs: 18, md: 20 },
            lineHeight: 1.65, color: 'text.primary', textAlign: 'left',
          }}>
            En HolaVeci, son los mismos{' '}
            <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>vecinos del barrio</Box>{' '}
            quienes proponen y{' '}
            <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>candidatizan las tiendas</Box>{' '}
            que quieren ver disponibles. Cuando una tienda acumula el apoyo de sus vecinos, HolaVeci la contacta con una invitación{' '}
            <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>validada por su propia comunidad</Box>.
          </Typography>

          <Typography sx={{
            fontFamily: 'Fraunces', fontSize: { xs: 18, md: 20 },
            lineHeight: 1.65, color: 'text.primary', textAlign: 'left', mt: 3,
          }}>
            Así se teje una{' '}
            <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>red barrial de confianza</Box>{' '}
            que reemplaza la publicidad efímera por una presencia digital permanente, donde el veci encuentra a la tienda justo{' '}
            <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>cuando la necesita</Box>.
          </Typography>
        </Container>
      </Box>

      {/* ---------- PILARES ---------- */}
      <Box id="pilares" sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="overline" color="warning.main" fontWeight={700}>
              Cómo pensamos HolaVeci
            </Typography>
            <Typography variant="h3" sx={{ fontFamily: 'Fraunces', mt: 1 }}>
              Del barrio, para el barrio
            </Typography>
            <Typography sx={{ mt: 2, color: 'text.secondary', maxWidth: 640, mx: 'auto' }}>
              Pensado desde el tendero real, no desde una torre de oficinas.
            </Typography>
          </Box>

          <Grid container spacing={3} alignItems="stretch">
            {[
              {
                titulo: 'El 100% de lo que vende, para usted',
                cuerpo: 'Sin comisión sobre las ventas. El tendero se queda con toda la plata de cada pedido.',
              },
              {
                titulo: 'El fiado, con memoria clara',
                cuerpo: 'Digitalizamos el fiado sin romperlo. El tendero decide a quién le fía. Cada abono queda anotado y confirmado por ambas partes.',
              },
              {
                titulo: 'Su domiciliario, no el nuestro',
                cuerpo: 'El que ya reparte para la tienda sigue haciéndolo. No metemos gente extraña al ecosistema del negocio.',
              },
              {
                titulo: 'Los vecinos proponen su tienda',
                cuerpo: 'Los vecinos del barrio candidatizan las tiendas de su cuadra desde la app. Cuando una tienda acumula suficientes votos, la buscamos.',
              },
              {
                titulo: 'La relación humana intacta',
                cuerpo: 'Chat directo entre el veci y su tendero. Sin bots, sin call center, sin intermediarios que estorben la conversación de siempre.',
              },
              {
                titulo: 'Cuota fija, sin sorpresas',
                cuerpo: 'Un solo precio mensual para el tendero. Sin porcentajes ocultos, sin tarifas por transacción, sin letra chica.',
              },
            ].map((pilar, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Card sx={{ height: '100%', borderLeft: 4, borderColor: 'primary.main' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontFamily: 'Fraunces', color: 'primary.dark', mb: 1.5 }}>
                      {pilar.titulo}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {pilar.cuerpo}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Alert severity="info" variant="outlined" sx={{ mt: 4, maxWidth: 720, mx: 'auto' }}>
            Si eres <b>veci</b> (cliente), la app siempre es <b>gratis</b> para ti.
            Solo pagas los productos que compras en cada negocio.
          </Alert>
        </Container>
      </Box>

      {/* ---------- CTA REGISTRO ---------- */}
      <Box id="registro" sx={{ bgcolor: '#FBF7F0', py: { xs: 6, md: 8 }, borderTop: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="overline" color="text.secondary">Cómo entrar</Typography>
            <Typography variant="h3" sx={{ fontFamily: 'Fraunces', mt: 1 }}>
              Elige tu perfil y arranquemos
            </Typography>
          </Box>

          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
              <Card sx={{ height: '100%', width: '100%',
                          border: '2px solid', borderColor: 'primary.main',
                          display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column',
                                    flexGrow: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box sx={{
                      width: 56, height: 56, borderRadius: 2, bgcolor: 'primary.main',
                      display: 'grid', placeItems: 'center', color: '#fff',
                    }}>
                      <StorefrontIcon sx={{ fontSize: 30 }} />
                    </Box>
                    <Box sx={{
                      bgcolor: 'primary.main', color: '#fff',
                      px: 1.5, py: 0.5, borderRadius: 1,
                      fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                    }}>RECOMENDADO</Box>
                  </Stack>
                  <Typography variant="overline" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    HolaVeci ·{' '}
                    <Box component="span" sx={{
                      display: 'inline-block',
                      bgcolor: 'primary.main', color: '#fff',
                      px: 1, py: 0.15, ml: 0.25,
                      borderRadius: 1,
                      fontWeight: 700,
                    }}>NEGOCIO</Box>
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 0.5, fontFamily: 'Fraunces' }}>
                    Quiero vender en línea a mi barrio
                  </Typography>
                  <Typography sx={{ mt: 1.5, color: 'text.secondary', flexGrow: 1 }}>
                    Catálogo digital, pedidos organizados, control de fiado por veci y
                    domiciliarios propios. Presencia permanente en tu sector.
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => setDialogDescarga('negocio')}
                    sx={{ mt: 3 }}
                  >
                    Registrar mi negocio
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
              <Card sx={{ height: '100%', width: '100%',
                          display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column',
                                    flexGrow: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box sx={{
                      width: 56, height: 56, borderRadius: 2, bgcolor: 'warning.main',
                      display: 'grid', placeItems: 'center', color: '#fff',
                    }}>
                      <ShoppingBasketIcon sx={{ fontSize: 30 }} />
                    </Box>
                    <Box sx={{
                      bgcolor: 'warning.main', color: '#fff',
                      px: 1.5, py: 0.5, borderRadius: 1,
                      fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                    }}>GRATIS</Box>
                  </Stack>
                  <Typography variant="overline" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    HolaVeci ·{' '}
                    <Box component="span" sx={{
                      display: 'inline-block',
                      bgcolor: 'warning.main', color: '#fff',
                      px: 1, py: 0.15, ml: 0.25,
                      borderRadius: 1,
                      fontWeight: 700,
                    }}>VECI</Box>
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 0.5, fontFamily: 'Fraunces' }}>
                    Quiero pedirle al negocio del barrio
                  </Typography>
                  <Typography sx={{ mt: 1.5, color: 'text.secondary', flexGrow: 1 }}>
                    Directorio de negocios de tu sector, carrito multi-negocio, historial y
                    saldo de fiado. Y si tu tienda favorita todavía no está,{' '}
                    <b>recomiéndala desde la app</b> para que la traigamos.
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    color="warning"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => setDialogDescarga('veci')}
                    sx={{ mt: 3, color: '#fff' }}
                  >
                    Bajar la app · Gratis
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
              <Card sx={{ height: '100%', width: '100%',
                          display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column',
                                    flexGrow: 1 }}>
                  <Box sx={{
                    width: 56, height: 56, borderRadius: 2, bgcolor: 'secondary.main',
                    display: 'grid', placeItems: 'center', color: 'primary.dark', mb: 2,
                  }}>
                    <PointOfSaleIcon sx={{ fontSize: 30 }} />
                  </Box>
                  <Typography variant="overline" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    HolaVeci ·{' '}
                    <Box component="span" sx={{
                      display: 'inline-block',
                      bgcolor: 'secondary.main', color: 'primary.dark',
                      px: 1, py: 0.15, ml: 0.25,
                      borderRadius: 1,
                      fontWeight: 700,
                    }}>LOCAL</Box>
                  </Typography>
                  <Typography variant="h5" sx={{ mt: 0.5, fontFamily: 'Fraunces' }}>
                    Quiero organizar la venta en mi local
                  </Typography>
                  <Typography sx={{ mt: 1.5, color: 'text.secondary', flexGrow: 1 }}>
                    Caja con apertura y cierre diario, inventario, facturación y ticket
                    térmico. La venta en sitio, bajo control.
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => abrirRegistroPOS('local')}
                    sx={{ mt: 3,
                          bgcolor: 'secondary.main',
                          color: 'primary.dark',
                          fontWeight: 700,
                          '&:hover': { bgcolor: 'secondary.dark', color: '#fff' } }}
                  >
                    Conocer Local
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* ---------- BANNER PAQUETE HOLAVECI 360 ---------- */}
          <Box sx={{
            mt: 5, p: { xs: 3, md: 4 }, borderRadius: 3,
            background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 50%, #388E3C 100%)',
            color: '#fff', position: 'relative', overflow: 'hidden',
          }}>
            {/* Badge diagonal decorativo */}
            <Box sx={{
              position: 'absolute', top: -30, right: -50, width: 200, height: 60,
              bgcolor: 'secondary.main', transform: 'rotate(35deg)',
              display: 'grid', placeItems: 'center',
              color: 'primary.dark', fontWeight: 800, fontSize: 12, letterSpacing: 1,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }}>
              AHORRA $20.000
            </Box>

            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 2 }}>
                  Paquete completo
                </Typography>
                <Typography variant="h3" sx={{ fontFamily: 'Fraunces', mt: 0.5, fontWeight: 600 }}>
                  HolaVeci <Box component="span" sx={{ color: 'secondary.main' }}>360°</Box>
                </Typography>
                <Typography sx={{ mt: 1.5, fontSize: { xs: 15, md: 17 }, opacity: 0.95, maxWidth: 520 }}>
                  <Box component="span" sx={{
                    display: 'inline-block',
                    bgcolor: 'primary.main', color: '#fff',
                    px: 1, py: 0.15,
                    borderRadius: 1,
                    fontWeight: 700,
                  }}>Negocio</Box>
                  {' '}para vender en línea a tu barrio, más{' '}
                  <Box component="span" sx={{
                    display: 'inline-block',
                    bgcolor: 'secondary.main', color: 'primary.dark',
                    px: 1, py: 0.15,
                    borderRadius: 1,
                    fontWeight: 700,
                  }}>Local</Box>
                  {' '}para organizar la venta en el mostrador. Un solo inventario, un solo control, todo tu negocio en HolaVeci.
                </Typography>

                {/* Comparativa de precios */}
                <Box sx={{ mt: 3, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, p: 2.5 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={4}>
                      <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>Negocio solo</Typography>
                      <Typography variant="h6" sx={{ opacity: 0.7, textDecoration: 'line-through' }}>
                        $30.000
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>Local solo</Typography>
                      <Typography variant="h6" sx={{ opacity: 0.7, textDecoration: 'line-through' }}>
                        $60.000
                      </Typography>
                    </Grid>
                    <Grid item xs={4} sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" sx={{ color: 'secondary.main', fontWeight: 700, display: 'block' }}>
                        HolaVeci 360°
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff' }}>
                        $70.000
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.85 }}>/ mes</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={12} md={5} sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                <Stack spacing={1.5} sx={{ mb: 3 }}>
                  {[
                    'Presencia digital en tu barrio',
                    'Caja y facturación en el mostrador',
                    'Un solo inventario compartido',
                    'Todas las mejoras futuras incluidas',
                  ].map((t, i) => (
                    <Stack key={i} direction="row" spacing={1.5} alignItems="center"
                           justifyContent={{ xs: 'center', md: 'flex-end' }}>
                      <Typography sx={{ fontSize: 14, opacity: 0.95 }}>{t}</Typography>
                      <CheckCircleIcon sx={{ color: 'secondary.main', fontSize: 18 }} />
                    </Stack>
                  ))}
                </Stack>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => abrirRegistroPOS('360')}
                  sx={{ bgcolor: 'secondary.main', color: 'primary.dark',
                        fontWeight: 700, px: 4,
                        '&:hover': { bgcolor: 'secondary.light' } }}
                >
                  Quiero HolaVeci 360°
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* ---------- SECCIÓN NOMINACIÓN COMUNITARIA ---------- */}
      <Box id="nominacion" sx={{
        py: { xs: 5, md: 7 },
        background: 'linear-gradient(135deg, #F57C00 0%, #FB8C00 100%)',
        color: '#fff',
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
              <Box sx={{
                width: 96, height: 96, borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.15)', display: 'inline-grid',
                placeItems: 'center', border: '2px solid rgba(255,255,255,0.35)',
              }}>
                <Typography sx={{ fontSize: 48 }}>📍</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 2 }}>
                Punto clave de HolaVeci
              </Typography>
              <Typography variant="h4" sx={{ fontFamily: 'Fraunces', mt: 0.5, fontWeight: 600 }}>
                ¿No está tu tienda favorita? <br />
                <Box component="span" sx={{ fontStyle: 'italic' }}>Recomiéndala tú mismo.</Box>
              </Typography>
              <Typography sx={{ mt: 2, fontSize: { xs: 15, md: 17 }, opacity: 0.95, maxWidth: 620 }}>
                Desde la app veci puedes proponer las tiendas de tu barrio que quieres ver en
                HolaVeci para pedirles a domicilio. Cuando una tienda acumula recomendaciones
                de sus vecinos, la contactamos con evidencia de que su sector la está pidiendo.
              </Typography>
            </Grid>
            <Grid item xs={12} md={3} sx={{ textAlign: { xs: 'center', md: 'right' } }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => setDialogDescarga('veci')}
                sx={{ bgcolor: '#fff', color: 'warning.dark', fontWeight: 700,
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.92)' } }}
              >
                Bajar la app · Gratis
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ---------- SECCIÓN HOLAVECI LOCAL ---------- */}
      <Box id="local" sx={{
        py: { xs: 6, md: 8 },
        bgcolor: 'background.default',
        borderTop: '1px solid', borderColor: 'divider',
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 2 }}>
              Conoce HolaVeci Local
            </Typography>
            <Typography variant="h3" sx={{ fontFamily: 'Fraunces', mt: 1 }}>
              ¿Además vendes en{' '}
              <Box component="span" sx={{ color: 'primary.main' }}>mostrador</Box>?
            </Typography>
            <Typography sx={{ mt: 2, color: 'text.secondary', maxWidth: 640, mx: 'auto' }}>
              <b>HolaVeci Local</b> agrega caja con apertura y cierre diario, inventario y
              facturación a tu negocio. Un stock, dos canales. También funciona por separado
              si aún no vendes en línea.
            </Typography>
          </Box>

          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ p: 2, bgcolor: '#fff' }}>
                <AnimacionPOSCompacto />
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              {/* Features en lista discreta */}
              <Stack spacing={2}>
                {[
                  { i: <PointOfSaleIcon />,   t: 'Punto de venta',
                    d: 'Con apertura y cierre diario, arqueo y ticket térmico imprimible.' },
                  { i: <Inventory2Icon />,    t: 'Un solo inventario',
                    d: 'El stock que muestra HolaVeci se actualiza al vender en caja, y al revés.' },
                  { i: <ReceiptLongIcon />,   t: 'Facturación fiscal',
                    d: 'Datos de tu empresa (NIT, régimen) para emitir tickets en regla.' },
                  { i: <AssessmentIcon />,    t: 'Reportes unificados',
                    d: 'Venta en línea + venta en sitio en una sola vista, exportable a PDF/Excel.' },
                ].map((f, idx) => (
                  <Stack key={idx} direction="row" spacing={2} alignItems="flex-start">
                    <Box sx={{
                      width: 40, height: 40, borderRadius: 1.5,
                      bgcolor: 'primary.main', color: '#fff',
                      display: 'grid', placeItems: 'center', flexShrink: 0,
                    }}>
                      {React.cloneElement(f.i, { sx: { fontSize: 22 } })}
                    </Box>
                    <Box>
                      <Typography fontWeight={700}>{f.t}</Typography>
                      <Typography variant="body2" color="text.secondary">{f.d}</Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>

              <Stack direction="row" spacing={2} sx={{ mt: 4, flexWrap: 'wrap', gap: 1.5 }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => abrirRegistroPOS('local')}
                >
                  Activar HolaVeci Local
                </Button>
                <Button
                  variant="text"
                  size="large"
                  href={`${URL_HOLAVECI_LOCAL}/login`}
                  target="_blank"
                  rel="noopener"
                  sx={{ color: 'text.secondary' }}
                >
                  Ya tengo HolaVeci Local →
                </Button>
              </Stack>

              <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', mt: 2 }}>
                Puedes tener HolaVeci Negocio solo, HolaVeci Local solo, o los dos juntos
                con HolaVeci 360°.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ---------- FOOTER ---------- */}
      <Box component="footer" sx={{
        bgcolor: 'primary.dark', color: '#FBF7F0', mt: 4,
      }}>
        <Container maxWidth="lg" sx={{ py: { xs: 5, md: 6 } }}>
          <Grid container spacing={4}>
            {/* Marca */}
            <Grid item xs={12} md={4}>
              <Logo light />
              <Typography variant="body2" sx={{ mt: 2, color: 'rgba(251, 247, 240, 0.75)', lineHeight: 1.6 }}>
                Tu barrio en la palma de tu mano, hecho por quienes lo habitan.
                Una red barrial que crece con la confianza de siempre.
              </Typography>
              <Chip
                label="Piloto Bucaramanga · 2026"
                size="small"
                sx={{
                  mt: 2, bgcolor: 'rgba(244, 178, 60, 0.15)',
                  color: 'warning.light', borderColor: 'warning.main',
                  border: '1px solid',
                }}
              />
            </Grid>

            {/* Producto */}
            <Grid item xs={6} md={2}>
              <Typography variant="overline" sx={{ color: 'warning.light', fontWeight: 700 }}>
                HolaVeci
              </Typography>
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                <Link href="#pilares" onClick={(e) => { e.preventDefault(); scrollA('pilares'); }}
                      sx={{ color: '#FBF7F0', opacity: 0.85, fontSize: 14, textDecoration: 'none',
                            '&:hover': { opacity: 1, textDecoration: 'underline' } }}>
                  Cómo pensamos
                </Link>
                <Link href="#registro" onClick={(e) => { e.preventDefault(); scrollA('registro'); }}
                      sx={{ color: '#FBF7F0', opacity: 0.85, fontSize: 14, textDecoration: 'none',
                            '&:hover': { opacity: 1, textDecoration: 'underline' } }}>
                  Cómo entrar
                </Link>
                <Link component="button" onClick={() => navigate('/login')}
                      sx={{ color: '#FBF7F0', opacity: 0.85, fontSize: 14, textAlign: 'left',
                            textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer',
                            '&:hover': { opacity: 1, textDecoration: 'underline' } }}>
                  Iniciar sesión
                </Link>
              </Stack>
            </Grid>

            {/* Empresa */}
            <Grid item xs={6} md={3}>
              <Typography variant="overline" sx={{ color: 'warning.light', fontWeight: 700 }}>
                Redeprog SAS
              </Typography>
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                <Typography variant="body2" sx={{ color: '#FBF7F0', opacity: 0.85, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOnIcon fontSize="small" />
                  Bucaramanga, Colombia
                </Typography>
                <Link href="mailto:contacto@holaveci.co"
                      sx={{ color: '#FBF7F0', opacity: 0.85, fontSize: 14, textDecoration: 'none',
                            display: 'flex', alignItems: 'center', gap: 1,
                            '&:hover': { opacity: 1, textDecoration: 'underline' } }}>
                  <MailOutlineIcon fontSize="small" />
                  contacto@holaveci.co
                </Link>
              </Stack>
            </Grid>

            {/* Legal */}
            <Grid item xs={12} md={3}>
              <Typography variant="overline" sx={{ color: 'warning.light', fontWeight: 700 }}>
                Legal
              </Typography>
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                <Link component="button" onClick={() => setLegalAbierto('TC_VECI')}
                      sx={{ color: '#FBF7F0', opacity: 0.85, fontSize: 14, textAlign: 'left',
                            textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer',
                            '&:hover': { opacity: 1, textDecoration: 'underline' } }}>
                  Términos y condiciones (veci)
                </Link>
                <Link component="button" onClick={() => setLegalAbierto('TC_NEGOCIO')}
                      sx={{ color: '#FBF7F0', opacity: 0.85, fontSize: 14, textAlign: 'left',
                            textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer',
                            '&:hover': { opacity: 1, textDecoration: 'underline' } }}>
                  Términos y condiciones (negocio)
                </Link>
                <Link component="button" onClick={() => setLegalAbierto('POLITICA_DATOS')}
                      sx={{ color: '#FBF7F0', opacity: 0.85, fontSize: 14, textAlign: 'left',
                            textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer',
                            '&:hover': { opacity: 1, textDecoration: 'underline' } }}>
                  Política de tratamiento de datos
                </Link>
                <Link component="button" onClick={() => setLegalAbierto('AUTORIZACION_DATOS')}
                      sx={{ color: '#FBF7F0', opacity: 0.85, fontSize: 14, textAlign: 'left',
                            textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer',
                            '&:hover': { opacity: 1, textDecoration: 'underline' } }}>
                  Autorización de datos personales
                </Link>
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ mt: 5, mb: 3, borderColor: 'rgba(251, 247, 240, 0.15)' }} />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1}
          >
            <Typography variant="caption" sx={{ color: 'rgba(251, 247, 240, 0.65)' }}>
              © {new Date().getFullYear()} Redeprog SAS · Todos los derechos reservados
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(251, 247, 240, 0.65)' }}>
              Hecho en Bucaramanga con {'❤'}
            </Typography>
          </Stack>
        </Container>
      </Box>

      {/* Dialog para ver documento legal */}
      <DialogoLegal codigo={legalAbierto} onCerrar={() => setLegalAbierto(null)} />

      <DialogoRegistroVeci
        abierto={dialogVeci}
        onCerrar={() => setDialogVeci(false)}
      />
      <DialogActivarPOS
        abierto={!!dialogPOS}
        enfoque={dialogPOS || 'local'}
        onCerrar={() => setDialogPOS(null)}
        onDescargarApp={() => { setDialogPOS(null); setDialogDescarga('negocio'); }}
      />
      <DialogDescargarApp
        abierto={!!dialogDescarga}
        onCerrar={() => setDialogDescarga(null)}
        producto={dialogDescarga || 'negocio'}
      />

      <ScrollTop />
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════
// DIALOG PARA VER DOCUMENTOS LEGALES DESDE EL FOOTER
// ═══════════════════════════════════════════════════════════

function DialogoLegal({ codigo, onCerrar }) {
  const [doc, setDoc]           = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    if (!codigo) { setDoc(null); setError(''); return; }
    let cancelado = false;
    setCargando(true);
    setError('');
    legal.documento(codigo)
      .then(d => { if (!cancelado) setDoc(d); })
      .catch(() => { if (!cancelado) setError('No se pudo cargar el documento.'); })
      .finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
  }, [codigo]);

  return (
    <Dialog open={!!codigo} onClose={onCerrar} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle sx={{ fontFamily: 'Fraunces' }}>
        {doc?.titulo || 'Documento legal'}
      </DialogTitle>
      <DialogContent dividers>
        {cargando && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="warning">{error}</Alert>}
        {doc?.contenidoHtml && (
          <Box
            dangerouslySetInnerHTML={{ __html: doc.contenidoHtml }}
            sx={{
              '& h1, & h2, & h3': { fontFamily: 'Fraunces', mt: 2 },
              '& p': { mb: 1.5 }, '& ul': { pl: 3 }, fontSize: 14, lineHeight: 1.6,
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCerrar}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════
// BOTÓN FLOTANTE "SUBIR" — aparece al hacer scroll ~200px
// ═══════════════════════════════════════════════════════════

function ScrollTop() {
  // useScrollTrigger dispara al pasar el umbral de scroll.
  // disableHysteresis:true → responde inmediato sin esperar cambios de dirección.
  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 200 });

  const subir = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Zoom in={trigger}>
      <Fab
        onClick={subir}
        color="primary"
        size="medium"
        aria-label="Subir al inicio"
        sx={{
          position: 'fixed',
          bottom: { xs: 20, md: 32 },
          right:  { xs: 20, md: 32 },
          zIndex: 1200,
          boxShadow: '0 6px 20px rgba(15,76,58,0.35)',
        }}
      >
        <KeyboardArrowUpIcon />
      </Fab>
    </Zoom>
  );
}
